/**
 * SupplierContactsDialog — zarządzanie moimi opiekunami u dostawcy (Etap B, 2026-05-13).
 *
 * Per-tenant override layer: każdy user MASKIL może mieć własnych opiekunów
 * w firmie dostawcy (np. Twój Jan Kowalski z Lena Lighting, mój Anna Nowak z tej samej Leny).
 * Globalny suppliers_kb pozostaje, ale fallback chain w RFQ zaczyna od kontaktu tego usera.
 *
 * Features:
 *   - Lista opiekunów z badges (primary, role)
 *   - Form: imię, rola, email, telefon, LinkedIn, notatki, preferred channel
 *   - is_primary toggle — ten kontakt domyślny przy generowaniu RFQ
 *   - Edit / Delete
 *   - Auto-discover button (wywołuje Serper) → wynik można przyjąć jako "global" lub utworzyć opiekuna
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import {
  UserPlus, Trash2, Edit, Star, Mail, Phone, Linkedin, Loader2, Search, Sparkles, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  listSupplierContacts,
  createSupplierContact,
  updateSupplierContact,
  deleteSupplierContact,
  discoverSupplierContact,
} from '../../lib/api';
import PlanLock from '../common/PlanLock';


const EMPTY_FORM = {
  name: '',
  role: '',
  email: '',
  phone: '',
  linkedin: '',
  notes: '',
  preferred_channel: 'email',
  is_primary: false,
};


function ContactCard({ contact, onEdit, onDelete, onSetPrimary, lang }) {
  return (
    <div className={`border rounded-lg p-3 ${contact.is_primary ? 'border-primary bg-primary/5' : 'border-border'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-medium text-sm">{contact.name}</span>
            {contact.is_primary && (
              <Badge className="bg-primary/20 text-primary border-primary/40 gap-1 text-[10px]">
                <Star className="w-2.5 h-2.5 fill-primary" />
                {lang === 'pl' ? 'PRIMARY' : 'PRIMARY'}
              </Badge>
            )}
            {contact.role && (
              <Badge variant="outline" className="text-[10px]">
                {contact.role}
              </Badge>
            )}
            <Badge variant="outline" className="text-[10px] gap-1">
              {contact.preferred_channel === 'phone' ? <Phone className="w-2.5 h-2.5" />
               : contact.preferred_channel === 'linkedin' ? <Linkedin className="w-2.5 h-2.5" />
               : <Mail className="w-2.5 h-2.5" />}
              {contact.preferred_channel}
            </Badge>
          </div>
          <div className="text-xs text-muted-foreground space-y-0.5">
            {contact.email && (
              <div className="flex items-center gap-1">
                <Mail className="w-3 h-3" />
                <a href={`mailto:${contact.email}`} className="hover:text-primary">{contact.email}</a>
              </div>
            )}
            {contact.phone && (
              <div className="flex items-center gap-1">
                <Phone className="w-3 h-3" />
                <a href={`tel:${contact.phone}`} className="hover:text-primary">{contact.phone}</a>
              </div>
            )}
            {contact.linkedin && (
              <div className="flex items-center gap-1">
                <Linkedin className="w-3 h-3" />
                <a href={contact.linkedin} target="_blank" rel="noreferrer" className="hover:text-primary truncate">
                  {contact.linkedin.replace(/^https?:\/\/(www\.)?/, '').slice(0, 40)}
                </a>
              </div>
            )}
            {contact.notes && (
              <p className="text-[11px] italic mt-1 line-clamp-2">{contact.notes}</p>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          {!contact.is_primary && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSetPrimary(contact)}
              title={lang === 'pl' ? 'Ustaw jako primary' : 'Set as primary'}
              className="h-7 w-7 p-0"
            >
              <Star className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(contact)}
            className="h-7 w-7 p-0"
            data-testid={`contact-edit-${contact.id}`}
          >
            <Edit className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(contact)}
            className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
            data-testid={`contact-delete-${contact.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}


function ContactForm({ form, setForm, lang }) {
  return (
    <div className="space-y-2.5">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">
            {lang === 'pl' ? 'Imię i nazwisko' : 'Name'} *
          </Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Jan Kowalski"
            className="h-8"
          />
        </div>
        <div>
          <Label className="text-xs">
            {lang === 'pl' ? 'Stanowisko / dział' : 'Role'}
          </Label>
          <Input
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            placeholder={lang === 'pl' ? 'Handlowiec / Inżynier' : 'Sales / Engineer'}
            className="h-8"
          />
        </div>
      </div>
      <div>
        <Label className="text-xs">Email</Label>
        <Input
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="jan.kowalski@firma.pl"
          className="h-8"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">{lang === 'pl' ? 'Telefon' : 'Phone'}</Label>
          <Input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+48 600 000 000"
            className="h-8"
          />
        </div>
        <div>
          <Label className="text-xs">LinkedIn</Label>
          <Input
            value={form.linkedin}
            onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
            placeholder="linkedin.com/in/..."
            className="h-8"
          />
        </div>
      </div>
      <div>
        <Label className="text-xs">{lang === 'pl' ? 'Notatki' : 'Notes'}</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder={lang === 'pl'
            ? 'np. odpowiada szybko / preferuje WhatsApp / kontakt po 14:00'
            : 'e.g. responds quickly / prefers WhatsApp / contact after 2 PM'}
          rows={2}
          className="text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">
            {lang === 'pl' ? 'Preferowany kanał' : 'Preferred channel'}
          </Label>
          <Select
            value={form.preferred_channel}
            onValueChange={(v) => setForm({ ...form, preferred_channel: v })}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="email">📧 Email</SelectItem>
              <SelectItem value="phone">📞 Telefon</SelectItem>
              <SelectItem value="linkedin">💼 LinkedIn</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer text-xs">
            <input
              type="checkbox"
              checked={form.is_primary}
              onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
            />
            <span>
              <Star className="w-3 h-3 inline mr-0.5" />
              {lang === 'pl'
                ? 'Primary (domyślny do RFQ)'
                : 'Primary (default for RFQ)'}
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}


function DiscoveryPanel({ supplier, lang, onAcceptAsContact }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  async function runDiscovery() {
    setRunning(true);
    try {
      const r = await discoverSupplierContact(supplier.id, {
        save_if_confident: true,
        min_confidence: 0.7,
      });
      setResult(r);
      if (r.saved_to_main_email) {
        toast.success(lang === 'pl'
          ? `Zapisano główny email: ${r.saved_email}`
          : `Saved main email: ${r.saved_email}`);
      } else if (r.discovery.best_email) {
        toast.info(lang === 'pl'
          ? `Znaleziono ${r.discovery.all_emails.length} adresów (confidence < 0.7 — nie zapisano)`
          : `Found ${r.discovery.all_emails.length} addresses (confidence < 0.7 — not saved)`);
      } else {
        toast.warning(lang === 'pl' ? 'Nie znaleziono emaili' : 'No emails found');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'pl' ? 'Błąd discovery' : 'Discovery error'));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="border border-dashed border-border rounded-lg p-3 space-y-2 bg-muted/20">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs">
          <strong className="flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-primary" />
            {lang === 'pl' ? 'Auto-discovery (Google + KRS)' : 'Auto-discovery (Google + KRS)'}
          </strong>
          <p className="text-muted-foreground mt-0.5">
            {lang === 'pl'
              ? 'Globalna baza — działa raz na 24h przez cron. Ten przycisk wymusza natychmiast.'
              : 'Global DB — runs once per 24h via cron. This button forces immediate run.'}
          </p>
        </div>
        <Button
          onClick={runDiscovery}
          disabled={running}
          size="sm"
          variant="outline"
          className="gap-1 shrink-0"
        >
          {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
          {lang === 'pl' ? 'Szukaj teraz' : 'Search now'}
        </Button>
      </div>
      {result && result.discovery && (
        <div className="space-y-1.5 pt-2 border-t border-border">
          {result.discovery.all_emails?.length > 0 ? (
            <>
              <div className="text-[11px] text-muted-foreground">
                {lang === 'pl' ? 'Znalezione' : 'Found'}:{' '}
                {result.discovery.all_emails.length} {lang === 'pl' ? 'adresów' : 'addresses'} ·{' '}
                {result.discovery.credits_used} {lang === 'pl' ? 'kredytów Serper' : 'credits used'}
              </div>
              {result.discovery.all_emails.slice(0, 5).map((e, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-2 p-1.5 bg-card rounded text-xs"
                >
                  <div className="flex-1 min-w-0">
                    <code className="text-primary">{e.email}</code>
                    <div className="text-[10px] text-muted-foreground">
                      conf: <strong>{(e.confidence * 100).toFixed(0)}%</strong>{' '}
                      ({e.source}) · {(e.reasons || [])[0]?.slice(0, 50)}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onAcceptAsContact && onAcceptAsContact(e)}
                    className="h-6 px-2 text-[10px]"
                    title={lang === 'pl' ? 'Dodaj jako mój opiekun' : 'Add as my contact'}
                  >
                    <UserPlus className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              {lang === 'pl' ? 'Brak wyników' : 'No results'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}


export default function SupplierContactsDialog({
  open,
  onClose,
  supplier,
  lang = 'pl',
}) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!supplier?.id) return;
    setLoading(true);
    try {
      const r = await listSupplierContacts(supplier.id);
      setContacts(r.items || []);
    } catch (err) {
      // 404 OK — empty
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [supplier?.id]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  function openCreate() {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, is_primary: contacts.length === 0 });  // first contact → default primary
    setFormOpen(true);
  }

  function openEdit(c) {
    setEditingId(c.id);
    setForm({
      name: c.name || '',
      role: c.role || '',
      email: c.email || '',
      phone: c.phone || '',
      linkedin: c.linkedin || '',
      notes: c.notes || '',
      preferred_channel: c.preferred_channel || 'email',
      is_primary: !!c.is_primary,
    });
    setFormOpen(true);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast.error(lang === 'pl' ? 'Imię wymagane' : 'Name required');
      return;
    }
    if (!form.email && !form.phone) {
      toast.error(lang === 'pl' ? 'Email lub telefon wymagany' : 'Email or phone required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateSupplierContact(supplier.id, editingId, form);
        toast.success(lang === 'pl' ? 'Zaktualizowano' : 'Updated');
      } else {
        await createSupplierContact(supplier.id, form);
        toast.success(lang === 'pl' ? 'Dodano opiekuna' : 'Contact added');
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'pl' ? 'Błąd zapisu' : 'Save error'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(c) {
    if (!window.confirm(lang === 'pl'
      ? `Usunąć opiekuna "${c.name}"?`
      : `Delete contact "${c.name}"?`)) return;
    try {
      await deleteSupplierContact(supplier.id, c.id);
      toast.success(lang === 'pl' ? 'Usunięto' : 'Deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  }

  async function handleSetPrimary(c) {
    try {
      await updateSupplierContact(supplier.id, c.id, { is_primary: true });
      toast.success(lang === 'pl' ? `${c.name} ustawiony jako primary` : `${c.name} set as primary`);
      await load();
    } catch (err) {
      toast.error('Error');
    }
  }

  function acceptDiscoveredEmail(emailEntry) {
    setEditingId(null);
    setForm({
      ...EMPTY_FORM,
      email: emailEntry.email,
      notes: lang === 'pl'
        ? `Znalezione przez auto-discovery (confidence ${(emailEntry.confidence * 100).toFixed(0)}%, source: ${emailEntry.source})`
        : `Found via auto-discovery (confidence ${(emailEntry.confidence * 100).toFixed(0)}%, source: ${emailEntry.source})`,
      is_primary: contacts.length === 0,
    });
    setFormOpen(true);
  }

  if (!supplier) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-primary" />
            {lang === 'pl' ? 'Moi opiekunowie' : 'My contacts'} — {supplier.name}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {lang === 'pl'
              ? 'Twoi osobiści opiekunowie w tej firmie. RFQ wysyłane do "primary" automatycznie. Pole globalne ' +
                '(suppliers_kb.main_email) jest fallback\'iem.'
              : 'Your personal contacts at this company. RFQs auto-sent to "primary". Global field is fallback.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Global info from suppliers_kb */}
          {(supplier.main_email || supplier.main_phone || supplier.website) && (
            <div className="text-[11px] text-muted-foreground p-2 bg-muted/20 rounded border border-border">
              <strong>{lang === 'pl' ? 'Globalne (z bazy)' : 'Global (DB)'}:</strong>{' '}
              {supplier.main_email && <span>📧 {supplier.main_email}</span>}{' '}
              {supplier.main_phone && <span>📞 {supplier.main_phone}</span>}{' '}
              {supplier.website && (
                <a href={supplier.website} target="_blank" rel="noreferrer" className="hover:text-primary">
                  🌐 {supplier.website.replace(/^https?:\/\//, '').slice(0, 30)}
                </a>
              )}
            </div>
          )}

          {/* My contacts list (PRO feature) */}
          <PlanLock feature="supplier_contacts" requiredPlan="pro" lang={lang}>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">
                  {lang === 'pl' ? 'Twoi opiekunowie' : 'Your contacts'}
                  {contacts.length > 0 && <span className="text-muted-foreground ml-1">({contacts.length})</span>}
                </h3>
                <Button onClick={openCreate} size="sm" className="gap-1 h-7" data-testid="contact-add">
                  <UserPlus className="w-3 h-3" />
                  {lang === 'pl' ? 'Dodaj' : 'Add'}
                </Button>
              </div>

              {loading ? (
                <>
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </>
              ) : contacts.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-xs">
                  {lang === 'pl'
                    ? 'Brak opiekunów — dodaj pierwszego lub użyj auto-discovery poniżej'
                    : 'No contacts yet — add one or use auto-discovery below'}
                </div>
              ) : (
                <div className="space-y-2">
                  {contacts.map((c) => (
                    <ContactCard
                      key={c.id}
                      contact={c}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                      onSetPrimary={handleSetPrimary}
                      lang={lang}
                    />
                  ))}
                </div>
              )}
            </div>
          </PlanLock>

          {/* Auto-discovery panel (PRO feature) */}
          <PlanLock feature="suppliers_auto_discovery" requiredPlan="pro" lang={lang}>
            <DiscoveryPanel
              supplier={supplier}
              lang={lang}
              onAcceptAsContact={acceptDiscoveredEmail}
            />
          </PlanLock>
        </div>

        {/* Inline form (collapsible) */}
        {formOpen && (
          <div className="border-t border-border pt-3 space-y-3 mt-2">
            <h3 className="text-sm font-semibold flex items-center gap-1">
              {editingId
                ? (lang === 'pl' ? 'Edytuj opiekuna' : 'Edit contact')
                : (lang === 'pl' ? 'Nowy opiekun' : 'New contact')}
            </h3>
            <ContactForm form={form} setForm={setForm} lang={lang} />
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFormOpen(false)}
                disabled={saving}
              >
                {lang === 'pl' ? 'Anuluj' : 'Cancel'}
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                size="sm"
                data-testid="contact-save"
                className="gap-1"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                {editingId
                  ? (lang === 'pl' ? 'Zapisz zmiany' : 'Save changes')
                  : (lang === 'pl' ? 'Dodaj opiekuna' : 'Add contact')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
